/**
 * Currency configuration and formatting utilities
 */

export const CURRENCY_CONFIG = {
  USD: {
    symbol: "$",
    locale: "en-US",
    position: "before",
    decimals: 2,
  },
  VND: {
    symbol: "₫",
    locale: "vi-VN",
    position: "after",
    decimals: 0,
  },
  EUR: {
    symbol: "€",
    locale: "de-DE",
    position: "after",
    decimals: 2,
  },
  GBP: {
    symbol: "£",
    locale: "en-GB",
    position: "before",
    decimals: 2,
  },
  JPY: {
    symbol: "¥",
    locale: "ja-JP",
    position: "before",
    decimals: 0,
  },
  SGD: {
    symbol: "S$",
    locale: "en-SG",
    position: "before",
    decimals: 2,
  },
} as const;

export type CurrencyCode = keyof typeof CURRENCY_CONFIG;

/**
 * A currency code {@link formatCurrency} can actually format.
 *
 * Every money field on the wire is typed `string`, while `formatCurrency` indexes its config by
 * the code — an unlisted one reads `undefined.locale` and throws. Callers that get their code
 * from an API response or a socket payload go through here rather than casting. VND is the
 * fallback because it is what this platform bills in.
 */
export function toCurrencyCode(value: string | undefined | null): CurrencyCode {
  return value && value in CURRENCY_CONFIG ? (value as CurrencyCode) : "VND";
}

/**
 * Formats a number as currency with proper locale and symbol
 * @param amount - The amount to format (in standard units, not cents)
 * @param currency - The currency code (default: VND)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: CurrencyCode = "VND",
): string {
  const config = CURRENCY_CONFIG[currency];

  return new Intl.NumberFormat(config.locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: config.decimals,
    maximumFractionDigits: config.decimals,
  }).format(amount);
}

/**
 * Converts minor units to standard currency units.
 * Handles zero-decimal currencies (VND, JPY) correctly — returns as-is.
 * @param minorUnits - Amount in smallest currency unit
 * @param currency - Currency code (default: VND)
 * @returns Amount in standard units
 */
export function centsToCurrency(
  minorUnits: number,
  currency: CurrencyCodeMetadata = "VND",
): number {
  return fromMinorUnits(minorUnits, currency);
}

// Currency metadata (matches backend CurrencyMetadata.cs)
export const CURRENCY_METADATA = {
  USD: { decimals: 2, minorUnit: "cents", symbol: "$" },
  VND: { decimals: 0, minorUnit: "đồng", symbol: "₫" },
  JPY: { decimals: 0, minorUnit: "yen", symbol: "¥" },
  EUR: { decimals: 2, minorUnit: "cents", symbol: "€" },
  KWD: { decimals: 3, minorUnit: "fils", symbol: "KD" },
  GBP: { decimals: 2, minorUnit: "pence", symbol: "£" },
  SGD: { decimals: 2, minorUnit: "cents", symbol: "S$" },
} as const;

export type CurrencyCodeMetadata = keyof typeof CURRENCY_METADATA;

/**
 * Convert display amount to smallest currency unit
 *
 * @example toMinorUnits(10.50, 'USD') => 1050 (cents)
 * @example toMinorUnits(10000, 'VND') => 10000 (đồng, no conversion)
 * @example toMinorUnits(1.000, 'KWD') => 1000 (fils, 3 decimals)
 */
export function toMinorUnits(
  amount: number,
  currency: CurrencyCodeMetadata,
): number {
  const metadata = CURRENCY_METADATA[currency];
  return Math.round(amount * Math.pow(10, metadata.decimals));
}

/**
 * Convert smallest currency unit to display amount
 *
 * @example fromMinorUnits(1050, 'USD') => 10.50
 * @example fromMinorUnits(10000, 'VND') => 10000
 * @example fromMinorUnits(1000, 'KWD') => 1.000
 */
export function fromMinorUnits(
  amount: number,
  currency: CurrencyCodeMetadata,
): number {
  const metadata = CURRENCY_METADATA[currency];
  return amount / Math.pow(10, metadata.decimals);
}

/**
 * Number of decimal places a currency is billed in. Reads whichever of the two currency tables
 * knows the code, and falls back to 2 — the value most currencies use — for an unknown one.
 *
 * @param currency - ISO 4217 code.
 * @returns Decimal places (0 for VND/JPY, 2 for USD/EUR, 3 for KWD).
 */
export function getCurrencyDecimals(currency: string): number {
  const code = currency?.toUpperCase();
  const config = CURRENCY_CONFIG[code as CurrencyCode];
  if (config) return config.decimals;
  const metadata = CURRENCY_METADATA[code as CurrencyCodeMetadata];
  if (metadata) return metadata.decimals;
  return 2;
}

/**
 * Rounds a monetary amount to the currency's smallest unit, mirroring the backend's
 * `MoneyCalculator.Round`: floor for zero-decimal currencies (VND, JPY), otherwise round to the
 * currency's decimal places.
 *
 * Use this for every amount the UI derives itself (VAT, discounts, cycle totals). A plain
 * `Math.floor` matches the backend only for VND — for USD it would drop cents from the quoted tax
 * and total, so the customer would be shown a figure the order does not charge.
 *
 * @param amount - The raw amount in display units.
 * @param currency - ISO 4217 code.
 * @returns The amount rounded the same way the backend persists it.
 */
export function roundToCurrency(amount: number, currency: string): number {
  const decimals = getCurrencyDecimals(currency);
  if (decimals === 0) return Math.floor(amount);
  const factor = Math.pow(10, decimals);
  return Math.round(amount * factor) / factor;
}
